import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X, ShoppingBag, Plus, ChevronDown, Shirt, Footprints } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useGeo } from '../contexts/GeoContext';
import { toast } from 'sonner';
import { getSwatchStyle } from '../lib/colorMap';
import { thumb as optThumb } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: 'top', label: 'Top', icon: Shirt, categories: ['men', 'women', 'boys', 'girls'] },
  { key: 'bottom', label: 'Bottom', icon: Shirt, categories: ['men', 'women', 'boys', 'girls'] },
  { key: 'footwear', label: 'Footwear', icon: Footprints, categories: ['footwear'] },
  { key: 'hat', label: 'Hat / Cap', icon: Shirt, categories: ['hats'] },
  { key: 'accessory', label: 'Accessory', icon: Shirt, categories: ['accessories'] },
];

export default function ChangingRoomPage() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { formatPrice } = useGeo();
  const [outfit, setOutfit] = useState({});
  const [products, setProducts] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        setProducts(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // When outfit changes, compute color-matched suggestions for empty slots
  useEffect(() => {
    const filledItems = Object.values(outfit);
    if (filledItems.length === 0) {
      setSuggestions([]);
      return;
    }

    // Gather all colors from filled slots
    const outfitColors = new Set();
    filledItems.forEach(item => {
      (item.colors || []).forEach(c => outfitColors.add(c.toLowerCase()));
    });

    // For empty slots, find products with matching colors
    const emptySlots = SLOTS.filter(s => !outfit[s.key]);
    const matched = [];
    
    emptySlots.forEach(slot => {
      const slotProducts = products.filter(p => slot.categories.includes(p.category));
      slotProducts.forEach(p => {
        const pColors = (p.colors || []).map(c => c.toLowerCase());
        const hasMatch = pColors.some(c => outfitColors.has(c));
        if (hasMatch && !filledItems.some(f => f.id === p.id)) {
          matched.push({ ...p, _slot: slot.key });
        }
      });
    });

    setSuggestions(matched.slice(0, 6));
  }, [outfit, products]);

  const getSlotProducts = (slot) => {
    return products.filter(p => slot.categories.includes(p.category));
  };

  const selectProduct = (slotKey, product) => {
    setOutfit(prev => ({ ...prev, [slotKey]: product }));
    setActiveSlot(null);
  };

  const removeFromOutfit = (slotKey) => {
    setOutfit(prev => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
  };

  const addOutfitToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    const items = Object.values(outfit);
    if (items.length === 0) {
      toast.error('Add some items to your outfit first');
      return;
    }
    for (const item of items) {
      const size = item.sizes?.[0] || '';
      const color = item.colors?.[0] || '';
      await addToCart(item.id, 1, size, color);
    }
    toast.success(`Added ${items.length} items to cart!`);
  };

  const outfitTotal = Object.values(outfit).reduce((sum, item) => {
    const price = item.is_promo && item.promo_percentage
      ? item.price * (1 - item.promo_percentage / 100)
      : item.price;
    return sum + price;
  }, 0);

  const filledCount = Object.keys(outfit).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-96 skeleton border-2 border-[#0A0A0A]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="changing-room-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#525252]">
            Mix & Match
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            CHANGING ROOM
          </h1>
          <p className="text-[#525252] mt-2">Build your perfect outfit. See how everything looks together before you buy.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Outfit Preview */}
          <div className="lg:col-span-2">
            <div className="border-2 border-[#0A0A0A] bg-white p-6">
              <h2 className="font-bold text-lg mb-6 uppercase tracking-wider">Your Outfit</h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {SLOTS.map((slot) => {
                  const item = outfit[slot.key];
                  return (
                    <div key={slot.key} className="flex flex-col items-center">
                      <p className="text-xs font-bold uppercase tracking-wider mb-2 text-[#525252]">{slot.label}</p>
                      {item ? (
                        <div className="relative w-full aspect-square border-2 border-[#0A0A0A] bg-[#F5F5F5] overflow-hidden group">
                          <img src={optThumb(item.images?.[0])} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                          <button
                            onClick={() => removeFromOutfit(slot.key)}
                            className="absolute top-1 right-1 bg-white border border-[#0A0A0A] p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-${slot.key}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5">
                            <p className="text-white text-[9px] truncate font-semibold">{item.name}</p>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setActiveSlot(slot.key)}
                          className="w-full aspect-square border-2 border-dashed border-[#CCC] flex flex-col items-center justify-center gap-1 hover:border-[#0A0A0A] hover:bg-[#FAD4D4]/20 transition-all cursor-pointer"
                          data-testid={`add-${slot.key}`}
                        >
                          <Plus className="w-5 h-5 text-[#999]" />
                          <span className="text-[10px] text-[#999]">Add</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Outfit Summary */}
              {filledCount > 0 && (
                <div className="mt-6 pt-4 border-t-2 border-[#0A0A0A] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#525252]">{filledCount} item{filledCount > 1 ? 's' : ''} selected</p>
                    <p className="text-2xl font-black">{formatPrice(outfitTotal)}</p>
                  </div>
                  <Button
                    onClick={addOutfitToCart}
                    data-testid="add-outfit-to-cart"
                    className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-12 px-8 font-semibold hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Add Outfit to Cart
                  </Button>
                </div>
              )}
            </div>

            {/* Color-Matched Suggestions */}
            {suggestions.length > 0 && (
              <div className="mt-8 border-2 border-[#0A0A0A] bg-white p-6">
                <h3 className="font-bold text-sm uppercase tracking-wider mb-4">Suggested Matches</h3>
                <p className="text-xs text-[#525252] mb-4">Based on the colors in your outfit</p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {suggestions.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => selectProduct(p._slot, p)}
                      className="border-2 border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors overflow-hidden group"
                      data-testid={`suggestion-${p.id}`}
                    >
                      <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
                        <img src={optThumb(p.images?.[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      </div>
                      <div className="p-1.5">
                        <p className="text-[9px] uppercase text-[#999]">{p.category}</p>
                        <p className="text-[10px] font-semibold truncate">{p.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product Picker */}
          <div className="lg:col-span-1">
            <div className="border-2 border-[#0A0A0A] bg-white sticky top-24">
              <div className="px-4 py-3 border-b-2 border-[#0A0A0A] flex items-center justify-between">
                <h3 className="font-bold text-sm uppercase tracking-wider">
                  {activeSlot ? `Pick ${SLOTS.find(s => s.key === activeSlot)?.label}` : 'Select a Slot'}
                </h3>
                {activeSlot && (
                  <button onClick={() => setActiveSlot(null)} className="text-[#525252] hover:text-[#0A0A0A]">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {!activeSlot ? (
                <div className="p-4">
                  <p className="text-sm text-[#525252] mb-4">Click a + slot to start building your outfit, or choose a category:</p>
                  {SLOTS.map(slot => (
                    <button
                      key={slot.key}
                      onClick={() => setActiveSlot(slot.key)}
                      className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium hover:bg-[#FAD4D4] transition-colors border-b border-[#E5E5E5] last:border-0"
                      data-testid={`slot-picker-${slot.key}`}
                    >
                      {slot.label}
                      {outfit[slot.key] ? (
                        <span className="text-[10px] text-[#525252] bg-[#F5F5F5] px-2 py-0.5">Selected</span>
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#999]" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto">
                  {getSlotProducts(SLOTS.find(s => s.key === activeSlot)).map(p => {
                    const isSelected = outfit[activeSlot]?.id === p.id;
                    const price = p.is_promo && p.promo_percentage ? p.price * (1 - p.promo_percentage / 100) : p.price;
                    return (
                      <button
                        key={p.id}
                        onClick={() => selectProduct(activeSlot, p)}
                        className={`w-full flex items-center gap-3 p-3 text-left hover:bg-[#FAD4D4]/30 transition-colors border-b border-[#E5E5E5] ${isSelected ? 'bg-[#FAD4D4]/50' : ''}`}
                        data-testid={`pick-${p.id}`}
                      >
                        <div className="w-14 h-14 flex-shrink-0 border border-[#E5E5E5] bg-[#F5F5F5] overflow-hidden">
                          <img src={optThumb(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{p.name}</p>
                          <p className="text-xs text-[#525252]">{formatPrice(price)}</p>
                          {p.colors?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <div className="w-3 h-3 rounded-full border border-[#CCC] flex-shrink-0" style={getSwatchStyle(p.colors.join('/'))} title={p.colors.join(' / ')} />
                              <span className="text-[10px] text-[#525252] truncate">{p.colors.join(' / ')}</span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
